/**
 * Phase 6 cross-cutting PR 4 — proactive trigger locked copy.
 *
 * All 5 trigger title/message strings are **locked verbatim from
 * vanilla** `src/renderer/js/dashboard/proactive-checks.js`. Copy
 * editors must not "fix" them — emoji + en-dashes + apostrophes are
 * part of the voice.
 *
 * **D2 (OVERRIDES.md):** vanilla `race_countdown` emits notification
 * type `meet_reminder`, which is NOT in our 5-key allowlist. React
 * re-routes to `motivational_message` (audit-recommended Option A).
 * The copy is preserved verbatim; only the `type` field changes.
 *
 * Sourced from:
 *   - vanilla proactive-checks.js (158 lines, audit-verified)
 *   - `.claude/references/phase-6-cross-cutting-design/audit/states.md`
 *   - `.claude/references/phase-6-cross-cutting-design/direction.md` §4
 */

import type { NotificationType } from './notifAllowlist';

export type ProactiveTriggerType =
  | 'comeback'
  | 'compliance_drop'
  | 'race_countdown'
  | 'streak_milestone'
  | 'pr_celebration'
  | PlanLifecycleTriggerType;

/**
 * NU-2 — the plan-lifecycle nudge trio (notification type `plan_nudge`).
 * NOT vanilla ports: new deterministic copy, flag-gated `plan_nudges`.
 */
export type PlanLifecycleTriggerType =
  | 'draft_next_week'
  | 'unlogged_sessions'
  | 'season_wrap';

export type SeasonWrapVariant = 'final_week' | 'after_season';

/** Locked streak milestones (verbatim from vanilla proactive-checks.js:11). */
export const STREAK_MILESTONES: ReadonlyArray<number> = [7, 14, 21, 30, 50, 100];

/** Per-day idempotency key prefix — vanilla writes `_proactive_last_<type>`. */
export const PROACTIVE_DEBOUNCE_KEY_PREFIX = '_proactive_last_';

/**
 * Locked copy templates per trigger type. Title + message follow
 * vanilla word-for-word; the React render layer interpolates the
 * named placeholders only.
 */
export interface ProactiveCopy {
  /** Notification title (e.g., "👋 It's been 7 days"). */
  title: string;
  /** Notification message body — may contain `{placeholder}` tokens. */
  message: string;
  /**
   * The notification `type` field — what the bell renders this as.
   * NOTE: race_countdown re-routes from vanilla's `meet_reminder`
   * to the allowlisted `motivational_message` per D2.
   */
  notificationType: NotificationType;
  /**
   * NU-2 — optional deep link, stored as the notification's
   * `metadata.href` (consulted by NotifRow for `plan_nudge` rows only,
   * through `sanitizeInternalHref`). Absent on the 5 legacy triggers.
   */
  href?: string;
  /**
   * NU-2 (anti-nag debounce) — the value the orchestrator compares the
   * `_proactive_last_<type>` key against (skip when equal) and writes
   * after firing. Absent → defaults to today's date (the legacy daily
   * debounce, unchanged for the 5 vanilla triggers). draft_next_week
   * uses the TARGET WEEK (fires once per week to draft, not daily
   * Thu–Sun); season_wrap uses the variant (each fires exactly once).
   */
  debounceValue?: string;
}

/** Comeback: locked from proactive-checks.js:61-64. */
export const comebackCopy = (daysAway: number): ProactiveCopy => ({
  title: `👋 It's been ${daysAway} days`,
  message: 'Your plan is ready when you are.',
  notificationType: 'motivational_message',
});

/** Compliance drop: locked from proactive-checks.js:88-92. */
export const complianceDropCopy = (): ProactiveCopy => ({
  title: '📉 Training shifted this week',
  message: 'Looks like things have been busy. Want to adjust the plan to fit your schedule?',
  notificationType: 'motivational_message',
});

/**
 * Race countdown: locked from proactive-checks.js:104-111. Daily label
 * is `today | tomorrow | in N days` based on `daysUntil`.
 *
 * **D2 reroute:** vanilla notificationType was `meet_reminder` — re-
 * mapped here to allowlisted `motivational_message`. Copy unchanged.
 * **B292 (2026-09-28):** title drops the meet name; format is
 * `🏁 Your next meet is ${label}.` (period, no exclamation mark).
 */
export const raceCountdownCopy = (daysUntil: number): ProactiveCopy => {
  const label = daysUntil === 0 ? 'today' : daysUntil === 1 ? 'tomorrow' : `in ${daysUntil} days`;
  return {
    title: `🏁 Your next meet is ${label}.`,
    message: 'Time to review your race plan and get mentally ready.',
    notificationType: 'motivational_message', // D2: was 'meet_reminder' in vanilla
  };
};

/** Streak milestone: locked from proactive-checks.js:124-130. */
export const streakMilestoneCopy = (streak: number): ProactiveCopy => ({
  title: `🔥 ${streak}-day streak`,
  message: 'Consistency builds fitness. Keep it going.',
  notificationType: 'motivational_message',
});

/** PR celebration: locked from proactive-checks.js:138-144. */
export const prCelebrationCopy = (distance: string, finishTime: string): ProactiveCopy => ({
  title: `🏆 New ${distance} PR`,
  message: `${finishTime}. Your training is paying off.`,
  notificationType: 'motivational_message',
});

// ── NU-2 — plan-lifecycle nudge copy (new, not vanilla ports) ─────────────

/** Draft-next-week: deep-links to the seeded draft composer for week N. */
export const draftNextWeekCopy = (nextWeek: number): ProactiveCopy => ({
  title: `📝 Time to draft Week ${nextWeek}`,
  message: `Next week isn't drafted yet. Tap to draft it with your coach.`,
  notificationType: 'plan_nudge',
  href: `/chat?draftWeek=${nextWeek}`,
  debounceValue: String(nextWeek),
});

/** Unlogged-sessions: daily (no debounceValue → today's date). */
export const unloggedSessionsCopy = (count: number): ProactiveCopy => ({
  title: `🗓️ ${count} sessions to log this week`,
  message: 'Mark them done or skipped so your streak and compliance stay honest.',
  notificationType: 'plan_nudge',
  href: '/today',
});

/**
 * Season-wrap: distinct titles per variant so the server's 20h
 * (user,type,title) dedup treats the final_week → after_season
 * transition as a NEW event rather than swallowing it.
 */
export const seasonWrapCopy = (variant: SeasonWrapVariant): ProactiveCopy =>
  variant === 'final_week'
    ? {
        title: '🏁 Final week: finish strong',
        message: "You've reached the last week of your season plan. One more push.",
        notificationType: 'plan_nudge',
        href: '/plan',
        debounceValue: 'final_week',
      }
    : {
        title: '🏁 Season complete: start your next block',
        message: 'Your season has wrapped. Review how it went, then start your next block.',
        notificationType: 'plan_nudge',
        href: '/plan',
        debounceValue: 'after_season',
      };

/**
 * Priority order (LOCKED · OVERRIDES.md note in direction.md §4):
 * pr_celebration > streak_milestone > race_countdown > comeback >
 * compliance_drop. When multiple triggers fire on the same load, the
 * highest-priority one becomes the primary `_proactiveHint` (consumed
 * by motivational-coach to bias the LLM prompt).
 *
 * Vanilla used implicit "first wins" by call order — React makes it
 * explicit so PR celebrations don't get swallowed by a same-day
 * comeback fire.
 */
export const PROACTIVE_PRIORITY: ReadonlyArray<ProactiveTriggerType> = [
  'pr_celebration',
  'streak_milestone',
  'race_countdown',
  'comeback',
  'compliance_drop',
  // NU-2 (D3) — the lifecycle trio appends at the END: chores never
  // outrank celebrations for the LLM-bias primaryHint. Internal order
  // season_wrap > draft_next_week > unlogged_sessions matches the
  // detectPlanNudge cap.
  'season_wrap',
  'draft_next_week',
  'unlogged_sessions',
];

/**
 * ⚖️ REL-13 (founder-ruled 2026-08-16) — THE CHORE SET.
 *
 * The lifecycle trio, named as a category because a category is what the
 * ruling is about: a chore is a nag with a MULTI-DAY WINDOW, so deferring one
 * by a day costs nothing. That is what makes it the loser when it collides
 * with a post-run note, which is about a thing that happened once.
 *
 * The other five — PR, streak, race countdown, comeback, compliance drop —
 * are celebrations and safety signals: time-bound, not about the run, and they
 * still outrank the echo. The decision itself lives in `lib/heroCoachLine.ts`.
 *
 * ⚠️ This is the same trio as the tail of `PROACTIVE_PRIORITY` above, and
 * `proactiveCopy.test.ts` pins that the two agree — so a fourth chore added to
 * one list and not the other fails loudly, instead of quietly acquiring
 * celebration-grade precedence over an athlete's post-run note.
 */
export const PROACTIVE_CHORE_TRIGGERS: ReadonlySet<ProactiveTriggerType> =
  new Set<ProactiveTriggerType>(['season_wrap', 'draft_next_week', 'unlogged_sessions']);

/** Narrowing predicate for the set above. Null-safe: no trigger is no chore. */
export function isChoreTrigger(t: ProactiveTriggerType | null | undefined): boolean {
  return !!t && PROACTIVE_CHORE_TRIGGERS.has(t);
}
